import api from "../axios";
import { BASE_URL } from "../../utils/constants";

// SCRUM-844: Reading Review v2 (session-based) - see "Reading Review v2 API"
// (Oct 13, 2026). Base path {BASE_URL}/rpm/{acc_id}/reading-review/. The
// account is passed explicitly rather than read from sessionStorage inside
// each call, so a session always talks to the account it was started on.

const base = (accId) => `/rpm/${accId}/reading-review`;

// 1 · Locations the reviewer can choose from.
export const getReviewLocations = async (accId) => {
  const { data } = await api.get(`${base(accId)}/locations/`);
  return data?.locations || [];
};

// 2 · Case manager dropdown + each one's open session (if any).
export const getReviewCaseManagers = async (accId, locationIds) => {
  const { data } = await api.get(`${base(accId)}/case-managers/`, {
    params: { location_ids: locationIds.join(",") },
  });
  return data?.case_managers || [];
};

// 3 · Start a new session (abandons this case manager's paused one).
export const startReviewSession = async (accId, { caseManagerId, locationIds }) => {
  const { data } = await api.post(`${base(accId)}/sessions/`, {
    case_manager_id: caseManagerId,
    location_ids: locationIds,
  });
  return data;
};

// 4 · Session object with navigation fields.
export const getReviewSession = async (accId, sessionId) => {
  const { data } = await api.get(`${base(accId)}/sessions/${sessionId}/`);
  return data;
};

// 5 · continue | pause | complete. `reason` applies to pause only.
export const setReviewSessionState = async (accId, sessionId, action, reason) => {
  const { data } = await api.post(`${base(accId)}/sessions/${sessionId}/state/`, {
    action,
    ...(reason ? { reason } : {}),
  });
  return data;
};

// 6 · Keep-alive ping (at most once a minute, only after real input).
export const pingReviewSessionActivity = async (accId, sessionId) => {
  const { data } = await api.post(`${base(accId)}/sessions/${sessionId}/activity/`);
  return data;
};

// 7 · Next / Previous / Session List / first load. Returns
// { session, patient, left_patient }.
export const navigateReviewSession = async (accId, sessionId, to, completeCurrent = false) => {
  const { data } = await api.post(`${base(accId)}/sessions/${sessionId}/navigate/`, {
    to,
    ...(completeCurrent ? { complete_current: true } : {}),
  });
  return data;
};

// 8 · Session List (not paginated).
export const getReviewSessionPatients = async (accId, sessionId, { search, sort } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (sort && sort !== "position") params.sort = sort;
  const { data } = await api.get(`${base(accId)}/sessions/${sessionId}/patients/`, { params });
  return data;
};

// 9 · The patient's locked readings, grouped by type.
export const getReviewPatientReadings = async (accId, sessionId, patientId, readingType) => {
  const { data } = await api.get(
    `${base(accId)}/sessions/${sessionId}/patients/${patientId}/readings/`,
    { params: readingType ? { reading_type: readingType } : {} }
  );
  return data;
};

// 10 · Save statuses. Any reading can be set to any status (Reviewed,
// Escalated, Not Reviewed) - including on a completed patient, which goes
// back to pending (and progress down) if a reading is left unmarked.
// NOTE: the Not Reviewed list's field name (`not_reviewed`) is assumed - the
// contract copy we have predates this change; adjust here if it differs.
export const saveReviewPatientActions = async (
  accId,
  sessionId,
  patientId,
  { reviewed = [], escalated = [], notReviewed = [], note, followUpSeconds } = {}
) => {
  const payload = { reviewed, escalated };
  if (notReviewed.length) payload.not_reviewed = notReviewed;
  if (note) payload.note = note;
  if (followUpSeconds != null) payload.follow_up_seconds = followUpSeconds;
  const { data } = await api.post(
    `${base(accId)}/sessions/${sessionId}/patients/${patientId}/actions/`,
    payload
  );
  return data;
};

// Page close: a keepalive fetch survives the unload (sendBeacon can't carry
// the bearer token). action/reason go in the query string, no body.
export const pauseReviewSessionOnPageClose = (accId, sessionId) => {
  const token = localStorage.getItem("authToken");
  if (!token || !accId || !sessionId) return;
  try {
    fetch(
      `${BASE_URL}${base(accId)}/sessions/${sessionId}/state/?action=pause&reason=page_closed`,
      { method: "POST", keepalive: true, headers: { Authorization: `Bearer ${token}` } }
    );
  } catch {
    // The page is going away; nothing useful to do with a failure here.
  }
};

// 409 conflicts carry a `code`: session_locked | session_paused | session_closed.
export const SESSION_CONFLICT = {
  LOCKED: "session_locked",
  PAUSED: "session_paused",
  CLOSED: "session_closed",
};

export const getSessionConflict = (error) => {
  const { status, data } = error?.response || {};
  if (status !== 409 || !data?.code) return null;
  return data;
};

// Best human-readable message from an API error ({detail} or {field: msg}).
export const getApiErrorMessage = (error, fallback = "Something went wrong. Please try again.") => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (data.detail) return data.detail;
  if (data.error) return data.error;
  const first = Object.values(data).find((v) => typeof v === "string");
  return first || fallback;
};
